/**
 * Collapsed rollup chip for a delegation parent kanban card.
 *
 * Shows compact mini-dots representing the aggregate status split of the
 * children (done / ready for review / in flight / not-yet-started), plus an `N subtasks`
 * count and an expand/collapse caret. Clicking the chip toggles to reveal
 * the full child list via `<DelegationChildList>` in the parent card.
 *
 * Spec: docs/specs/2026-04-16-session-state-audit.md §3.5.
 */

import { useCallback } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { TaskStatusGlyph } from "@/components/ui";
import type { TaskStatus } from "@/api/tasks";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";

/** Status buckets for the summary dots — in priority display order. */
const DONE_STATUSES: TaskStatus[] = ["done"];
const REVIEW_STATUSES: TaskStatus[] = ["in_review"];
const ACTIVE_STATUSES: TaskStatus[] = ["in_progress", "blocked"];
const PENDING_STATUSES: TaskStatus[] = ["todo", "backlog"];

interface DelegationRollupChipProps {
  counts_by_status: Partial<Record<TaskStatus, number>>;
  totalCount: number;
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}

function sumStatuses(
  counts: Partial<Record<TaskStatus, number>>,
  statuses: TaskStatus[],
): number {
  return statuses.reduce((acc, s) => acc + (counts[s] ?? 0), 0);
}

export function DelegationRollupChip({
  counts_by_status,
  totalCount,
  expanded,
  onToggle,
  className,
}: DelegationRollupChipProps) {
  const t = useT(framesTaskboardMessages);
  const done = sumStatuses(counts_by_status, DONE_STATUSES);
  const review = sumStatuses(counts_by_status, REVIEW_STATUSES);
  const active = sumStatuses(counts_by_status, ACTIVE_STATUSES);
  const pending = sumStatuses(counts_by_status, PENDING_STATUSES);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Don't let the parent kanban-card onClick fire (that opens the task
      // detail — the chip is a local UI toggle).
      e.stopPropagation();
      onToggle();
    },
    [onToggle],
  );

  const label = t("subtasks", { count: totalCount });
  const CaretIcon = expanded ? Icons.ChevronDown : Icons.ChevronRight;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-expanded={expanded}
      aria-label={t(expanded ? "collapseSubtasks" : "expandSubtasks", {
        count: totalCount,
      })}
      className={cn(
        "group/chip mt-1 flex w-full items-center gap-1.5 rounded",
        "border px-1.5 py-0.5",
        "text-appkit-mini text-muted transition-colors hover:bg-elevated",
        "focus:outline-none focus:ring-1",
        className,
      )}
      data-testid="delegation-rollup-chip"
    >
      <CaretIcon className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{label}</span>
      <span
        className="ml-auto flex items-center gap-0.5"
        data-testid="delegation-rollup-dots"
      >
        {done > 0 && (
          <span
            className="flex items-center gap-0.5"
            data-testid="delegation-rollup-done"
          >
            <TaskStatusGlyph status="done" size="sm" />
            {done > 1 && <span className="tabular-nums">{done}</span>}
          </span>
        )}
        {review > 0 && (
          <span
            className="flex items-center gap-0.5"
            data-testid="delegation-rollup-review"
          >
            <TaskStatusGlyph status="in_review" size="sm" />
            {review > 1 && <span className="tabular-nums">{review}</span>}
          </span>
        )}
        {active > 0 && (
          <span
            className="flex items-center gap-0.5"
            data-testid="delegation-rollup-active"
          >
            <TaskStatusGlyph status="in_progress" size="sm" />
            {active > 1 && <span className="tabular-nums">{active}</span>}
          </span>
        )}
        {pending > 0 && (
          <span
            className="flex items-center gap-0.5"
            data-testid="delegation-rollup-pending"
          >
            <TaskStatusGlyph status="todo" size="sm" />
            {pending > 1 && <span className="tabular-nums">{pending}</span>}
          </span>
        )}
      </span>
    </button>
  );
}
